// Top-of-dashboard issue search. A combobox that filters the project's
// issues by title (case-insensitive substring) or by issue number (the
// numeric `iid`, with or without a leading "#"). Selecting a result opens
// the issue page for that request.
//
// Search runs against the full issue set the dashboard already holds — no
// extra fetch — so it spans every stage, including shipped work the
// steering board hides under the Active filter.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";

import type { DashboardIssue } from "@loom/shared/dashboard";

import { useT } from "../../i18n/index.js";
import { matchIssues } from "../issueSearch";

import { Icon } from "./Icon";

interface IssueSearchProps {
  readonly issues: ReadonlyArray<DashboardIssue>;
  readonly onOpen: (issue: DashboardIssue) => void;
}

export const IssueSearch = ({
  issues,
  onOpen,
}: IssueSearchProps): JSX.Element => {
  const t = useT();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => matchIssues(issues, query), [issues, query]);

  // Clamp the active index whenever the result set shrinks.
  useEffect(() => {
    setActive((a) => (results.length === 0 ? 0 : Math.min(a, results.length - 1)));
  }, [results.length]);

  // Click outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const pick = useCallback(
    (issue: DashboardIssue): void => {
      onOpen(issue);
      setQuery("");
      setOpen(false);
    },
    [onOpen],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (results.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
        setActive((a) => (a + 1) % results.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setOpen(true);
        setActive((a) => (a - 1 + results.length) % results.length);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const issue = results[active];
        if (issue) pick(issue);
      }
    },
    [results, active, pick],
  );

  const showDropdown = open && query.trim() !== "";

  return (
    <div className="issue-search" ref={rootRef}>
      <div className="issue-search-field">
        <Icon name="search" size={14} />
        <input
          type="text"
          className="issue-search-input"
          placeholder={t("dashboard.search.placeholder")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="issue-search-results"
          aria-autocomplete="list"
        />
        {query !== "" && (
          <button
            type="button"
            className="issue-search-clear"
            title={t("dashboard.search.clear")}
            aria-label={t("dashboard.search.clearAria")}
            onClick={() => {
              setQuery("");
              setOpen(false);
            }}
          >
            <Icon name="x" size={13} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          className="issue-search-results"
          id="issue-search-results"
          role="listbox"
        >
          {results.length === 0 ? (
            <div className="issue-search-empty">{t("dashboard.search.empty")}</div>
          ) : (
            results.map((issue, idx) => (
              <button
                type="button"
                key={issue.iid}
                role="option"
                aria-selected={idx === active}
                className={`issue-search-row ${idx === active ? "active" : ""}`}
                onMouseEnter={() => setActive(idx)}
                onClick={() => pick(issue)}
              >
                <span className="issue-search-key">{issue.key}</span>
                <span className="issue-search-title">{issue.title}</span>
                <span className="issue-search-chat">
                  <Icon name="issue" size={12} /> {t("dashboard.search.open")}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
